import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Menu, X, Phone, LayoutDashboard, Sparkles } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { useAuth } from "@/hooks/use-auth";

interface SocialAccount {
  id: string;
  platform: string;
  username: string | null;
  profileUrl: string | null;
  isActive: boolean | null;
}

const HEADER_SOCIAL_ICONS: Record<string, { icon: string; color: string }> = {
  twitter: { icon: "𝕏", color: "#1DA1F2" },
  telegram: { icon: "✈", color: "#0088cc" },
  instagram: { icon: "📷", color: "#E4405F" },
  facebook: { icon: "f", color: "#1877F2" },
  linkedin: { icon: "in", color: "#0A66C2" },
  tiktok: { icon: "♪", color: "#FFFFFF" },
};

const navItems = [
  { label: "Companions", path: "/" },
  { label: "Availability", path: "/availability" },
  { label: "Services", path: "/services" },
  { label: "Safety", path: "/safety" },
  { label: "Recruitment", path: "/recruitment", highlight: true },
];

export function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [location] = useLocation();
  const { isAuthenticated, role } = useAuth();
  const siteConfig = useSiteConfig();

  const { data: socialAccounts = [] } = useQuery<SocialAccount[]>({
    queryKey: ["/api/social/accounts"],
    staleTime: 5 * 60 * 1000,
  });
  const activeSocials = socialAccounts.filter(a => a.isActive);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      data-testid="nav-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-[#0A0A0A]/95 backdrop-blur-md py-3 shadow-2xl border-b border-white/5"
          : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link href="/" data-testid="link-home" className="flex items-center gap-2 group">
          <div className="w-10 h-8 border-2 border-[#D4AF37] flex items-center justify-center rounded-sm group-hover:bg-gradient-to-br group-hover:from-[#D4AF37] group-hover:to-[#B48F1B] transition-all duration-300">
            <span className="text-[#D4AF37] group-hover:text-black font-bold text-xs px-1">
              LEM
            </span>
          </div>
          <span className="text-white font-medium tracking-[0.3em] text-[10px] uppercase hidden sm:inline">
            Luxury Escorts Manchester
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-10">
          {navItems.map((item) => (
            <Link
              key={item.label}
              href={item.path}
              data-testid={`link-nav-${item.label.toLowerCase()}`}
              className={`text-[10px] uppercase tracking-[0.2em] font-semibold transition-colors relative ${
                (item as any).highlight
                  ? "text-[#D4AF37] border border-[#D4AF37]/60 px-3 py-1.5 rounded-full hover:bg-[#D4AF37] hover:text-black flex items-center gap-1.5"
                  : location === item.path
                    ? "text-[#D4AF37]"
                    : "text-gray-400 hover:text-[#D4AF37]"
              }`}
            >
              {(item as any).highlight && (
                <>
                  <Sparkles size={10} />
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#D4AF37] rounded-full animate-ping" />
                  <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#D4AF37] rounded-full" />
                </>
              )}
              {(item as any).highlight ? "We're Hiring" : item.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {/* Social media icons */}
          {activeSocials.length > 0 && (
            <div className="hidden sm:flex items-center gap-1.5 mr-1">
              {activeSocials.slice(0, 4).map((account) => {
                const cfg = HEADER_SOCIAL_ICONS[account.platform];
                if (!cfg) return null;
                return (
                  <a
                    key={account.id}
                    href={account.profileUrl || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={account.platform}
                    className="w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-gray-400 hover:text-[#D4AF37] border border-gray-700/50 hover:border-[#D4AF37]/50 transition-all"
                  >
                    {cfg.icon}
                  </a>
                );
              })}
            </div>
          )}
          {/* Admin back button — only shown when logged in and on frontend pages */}
          {isAuthenticated && !location.startsWith("/admin") && !location.startsWith("/portal") && (
            <Link href={role === "hostess" ? "/portal/hostess" : role === "companion" ? "/portal/companion" : role === "driver" ? "/portal/driver" : "/admin"}
              className="hidden sm:flex items-center gap-2 border border-[#D4AF37]/50 text-[#D4AF37] px-4 py-2 rounded-full text-[9px] font-bold uppercase tracking-widest hover:bg-[#D4AF37] hover:text-black transition-all">
              <LayoutDashboard size={12} /> Dashboard
            </Link>
          )}
          {siteConfig.phoneEnabled && (
            <a
              href={`tel:${siteConfig.phoneNumber}`}
              data-testid="link-call"
              className="hidden sm:flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#B48F1B] text-black px-6 py-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest hover:brightness-110 transition-all shadow-lg shadow-[#D4AF37]/20"
            >
              <Phone size={12} />
              {siteConfig.phoneLabel}
            </a>
          )}
          <button
            data-testid="button-mobile-menu"
            className="md:hidden text-white"
            onClick={() => setIsMobileMenuOpen(true)}
          >
            <Menu size={24} />
          </button>
        </div>
      </div>

      {/* Mobile menu overlay with CSS transition */}
      <div
        className={`fixed inset-0 bg-[#0F0F0F] z-[60] p-6 flex flex-col transition-transform duration-300 ease-out ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex justify-end">
          <button
            type="button"
            title="Close menu"
            data-testid="button-close-menu"
            onClick={() => setIsMobileMenuOpen(false)}
            className="text-white"
          >
            <X size={32} />
          </button>
        </div>
        <div className="flex flex-col gap-8 mt-12 text-center">
          {navItems.map((item) => (
            <Link
              key={item.label}
              href={item.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`text-2xl font-light tracking-widest uppercase ${
                (item as any).highlight
                  ? "text-[#D4AF37] border border-[#D4AF37]/60 rounded-full py-3 mx-auto px-8 flex items-center justify-center gap-3 text-lg font-semibold"
                  : location === item.path ? "text-[#D4AF37]" : "text-white"
              }`}
            >
              {(item as any).highlight && <Sparkles size={16} />}
              {(item as any).highlight ? "We're Hiring!" : item.label}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
}
